import {
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react"
import { useRxCollection } from "rxdb-hooks"
import type { CollectionKey } from "src/types"
import { noticeError } from "src/utils"

export function useCollectionHandler<T>(
  collectionKey: CollectionKey,
) {
  const collection = useRxCollection<T>(collectionKey)
  const unsavedCollectionItems = useRef<Array<Partial<T>>>([])

  const upsertItem = useMemo(
    () => !collection
      ? undefined
      : async (item: Partial<T>) => {
        try {
          await collection.incrementalUpsert(item)
        } catch (e) {
          noticeError(
            e,
            "RxDBWebsocketUpsert",
            { item },
          )
        }
      },
    [collection],
  )

  // add the unsaved collection items to the collection when the collection is made available
  useEffect(
    () => {
      if (upsertItem && unsavedCollectionItems.current.length) {
        const unsavedItems = unsavedCollectionItems.current
        unsavedCollectionItems.current = []

        unsavedItems.forEach((item) => {
          void upsertItem(item)
        })
      }
    },
    [
      upsertItem,
      unsavedCollectionItems,
    ],
  )

  return useCallback(
    (item: Partial<T>) => {
      if (upsertItem) {
        void upsertItem(item)
      } else {
        unsavedCollectionItems.current.push(item)
      }
    },
    [
      upsertItem,
      unsavedCollectionItems,
    ],
  )
}
